import React, { useContext } from 'react';
import { LanguageContext } from '../../App';
import { TruckIcon, PackageIcon, UserIcon, ClockIcon } from 'lucide-react';
import { CountUp } from '../ui/CountUp';
export const StatisticsSection: React.FC = () => {
  const {
    language
  } = useContext(LanguageContext);
  const deliveryData = [{
    time: '00:00',
    orders: 12
  }, {
    time: '03:00',
    orders: 8
  }, {
    time: '06:00',
    orders: 15
  }, {
    time: '09:00',
    orders: 35
  }, {
    time: '12:00',
    orders: 45
  }, {
    time: '15:00',
    orders: 38
  }, {
    time: '18:00',
    orders: 42
  }, {
    time: '21:00',
    orders: 28
  }];
  const peakHoursData = [{
    hour: '8-10',
    deliveries: 85
  }, {
    hour: '10-12',
    deliveries: 120
  }, {
    hour: '12-14',
    deliveries: 150
  }, {
    hour: '14-16',
    deliveries: 135
  }, {
    hour: '16-18',
    deliveries: 145
  }, {
    hour: '18-20',
    deliveries: 130
  }, {
    hour: '20-22',
    deliveries: 95
  }];
  return <div className="space-y-6" data-id="element-565">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6" data-id="element-566">
        <StatCard title={language === 'en' ? 'Active Drivers' : 'السائقين النشطين'} value={120} change="+12%" timeframe={language === 'en' ? 'vs last week' : 'مقارنة بالأسبوع الماضي'} icon={<TruckIcon size={24} className="text-congress-blue-600" data-id="element-568" />} iconBg="bg-congress-blue-50" data-id="element-567" />
        <StatCard title={language === 'en' ? 'Completed Orders' : 'الطلبات المكتملة'} value={500} change="+8%" timeframe={language === 'en' ? 'vs last week' : 'مقارنة بالأسبوع الماضي'} icon={<PackageIcon size={24} className="text-regent-st-blue-600" data-id="element-570" />} iconBg="bg-regent-st-blue-50" data-id="element-569" />
        <StatCard title={language === 'en' ? 'Active Users' : 'المستخدمين النشطين'} value={500} change="+15%" timeframe={language === 'en' ? 'vs last week' : 'مقارنة بالأسبوع الماضي'} icon={<UserIcon size={24} className="text-green-600" data-id="element-572" />} iconBg="bg-green-50" data-id="element-571" />
        <StatCard title={language === 'en' ? 'Average Delivery Time' : 'متوسط وقت التوصيل'} value={28} suffix="m" change="-5%" timeframe={language === 'en' ? 'vs last week' : 'مقارنة بالأسبوع الماضي'} icon={<ClockIcon size={24} className="text-yellow-600" data-id="element-574" />} iconBg="bg-yellow-50" data-id="element-573" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-575">
        <ChartCard title={language === 'en' ? 'Delivery Trends' : 'اتجاهات التوصيل'} type="area" data={deliveryData} data-id="element-576" />
        <ChartCard title={language === 'en' ? 'Peak Hours' : 'ساعات الذروة'} type="bar" data={peakHoursData} data-id="element-577" />
      </div>
    </div>;
};
// Stat card component for consistent styling
const StatCard: React.FC<{
  title: string;
  value: number;
  suffix?: string;
  change: string;
  timeframe: string;
  icon: React.ReactNode;
  iconBg: string;
}> = ({
  title,
  value,
  suffix = '',
  change,
  timeframe,
  icon,
  iconBg
}) => <div className="bg-white p-4 md:p-6 rounded-lg border border-gray-200 shadow-sm" data-id="element-578">
    <div className="flex justify-between items-start" data-id="element-579">
      <div data-id="element-580">
        <p className="text-sm font-medium text-gray-600" data-id="element-581">{title}</p>
        <p className="mt-2 text-2xl md:text-3xl font-bold text-gray-900" data-id="element-582">
          <CountUp to={value} suffix={suffix} data-id="element-583" />
        </p>
        <div className="mt-2 flex items-center" data-id="element-584">
          <span className={`text-sm font-medium ${change.startsWith('+') ? 'text-green-600' : 'text-red-600'}`} data-id="element-585">
            {change}
          </span>
          <span className="text-sm text-gray-500 ml-1" data-id="element-586">{timeframe}</span>
        </div>
      </div>
      <div className={`p-3 rounded-full ${iconBg}`} data-id="element-587">{icon}</div>
    </div>
  </div>;
// Improved chart card component with more robust rendering
const ChartCard: React.FC<{
  title: string;
  type: 'area' | 'bar';
  data: any[];
}> = ({
  title,
  type,
  data
}) => {
  // Early return if no data
  if (!data || data.length === 0) {
    return <div className="bg-white p-4 md:p-6 rounded-lg border border-gray-200 shadow-sm" data-id="element-588">
        <h3 className="text-lg font-medium text-gray-900 mb-4" data-id="element-589">{title}</h3>
        <div className="h-64 bg-gray-50 rounded-lg border border-gray-100 flex items-center justify-center" data-id="element-590">
          <p className="text-gray-500" data-id="element-591">No data available</p>
        </div>
      </div>;
  }
  // Determine the property to use based on chart type
  const valueKey = type === 'area' ? 'orders' : 'deliveries';
  const labelKey = type === 'area' ? 'time' : 'hour';
  // Calculate maximum value with safety check
  const values = data.map(item => Number(item[valueKey] || 0));
  const maxValue = Math.max(...values, 1); // Ensure maxValue is at least 1 to avoid division by zero
  return <div className="bg-white p-4 md:p-6 rounded-lg border border-gray-200 shadow-sm" data-id="element-592">
      <h3 className="text-lg font-medium text-gray-900 mb-4" data-id="element-593">{title}</h3>
      <div className="h-64 bg-gray-50 rounded-lg border border-gray-100 flex items-end p-4 relative" data-id="element-594">
        {/* Y-axis labels */}
        <div className="absolute left-2 top-0 h-full flex flex-col justify-between text-xs text-gray-500 py-2" data-id="element-595">
          <span data-id="element-596">{maxValue}</span>
          <span data-id="element-597">{Math.floor(maxValue / 2)}</span>
          <span data-id="element-598">0</span>
        </div>
        {/* Chart bars */}
        <div className="flex items-end w-full h-full pl-6" data-id="element-599">
          {data.map((item, index) => {
          const value = Number(item[valueKey] || 0);
          // Ensure minimum visible height for non-zero values
          const heightPercentage = value === 0 ? 0 : Math.max(5, value / maxValue * 90);
          return <div key={index} className="flex flex-col items-center justify-end flex-1 min-w-0" style={{
            height: '100%'
          }} data-id="element-600">
                <div className={`w-full mx-1 rounded-t transition-all duration-300 ${type === 'area' ? 'bg-congress-blue-500' : 'bg-regent-st-blue-500'}`} style={{
              height: `${heightPercentage}%`,
              minHeight: value > 0 ? '4px' : '0'
            }} data-id="element-601">
                  {/* Value tooltip on hover */}
                  <div className="opacity-0 hover:opacity-100 absolute -top-6 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs rounded px-2 py-1" data-id="element-602">
                    {value}
                  </div>
                </div>
                <div className="text-xs text-gray-500 mt-2 truncate w-full text-center" data-id="element-603">
                  {item[labelKey]}
                </div>
              </div>;
        })}
        </div>
      </div>
    </div>;
};